import { useMemo } from 'react';

import {
  addFavourite,
  adminCalendar,
  adminName,
  allNotes,
  ContractTypes,
  getFilterEventsByType,
  getFilterPostedBy,
  getFilterShiftType,
  getIsWatchlisted,
  getMapUserFilterType,
  getTypeOfUser,
  MiscType,
  nameSelector,
  pharmacistCalendar,
  pharmacistHistory,
  pharmacistPDF,
  pharmacistReplacementsWeb,
  pharmacistReports,
  pharmacyAddFavourite,
  pharmacyCalendar,
  pharmacyHistory,
  PharmacyLegendVariant,
  pharmacyNotes,
  pharmacyPdf,
  pharmacyRemoveFavourite,
  pharmacyReplacementsWeb,
  pharmacyReports,
  removeFavourite,
  ToUserTypings,
  TypeOfUser,
  userGeneral,
  userPharmacyInfo,
  userPreferredLanguage,
} from '@pharmaplan/common';

import {
  filterByLongTerm,
  filterByPosted,
  filterByReplacementType,
  filterByShortTerm,
} from '@pharmaplan/common/helpers/utils';
import { useAppSelector } from './useAppSelector';

const useUser = () => {
  const userType = useAppSelector(getTypeOfUser);

  const pharmacistUser = useAppSelector(userGeneral);
  const pharmacyUser = useAppSelector(userPharmacyInfo);
  const adminsCalendar = useAppSelector(adminCalendar);
  const isWatchlisted = useAppSelector(getIsWatchlisted);

  const adminUser = useAppSelector(adminName);
  const generalUserName = useAppSelector(nameSelector);

  const activePharmacyCalendar = useAppSelector(pharmacyCalendar);
  const filterEventsByType = useAppSelector(getFilterEventsByType);
  const filterShiftType = useAppSelector(getFilterShiftType);

  const filteredPharmacyEvents = activePharmacyCalendar?.filter((ev) =>
    filterEventsByType?.includes(ev.type as `${PharmacyLegendVariant}`));

  const activePharmacistCalendar = useAppSelector(pharmacistCalendar);
  const filteredPharmacistEvents = activePharmacistCalendar?.filter(
    (ev) =>
      ev?.isInWatchlist,
  );

  const activePharmacistReports = useAppSelector(pharmacistReports);
  const activePharmacyReports = useAppSelector(pharmacyReports);

  const pharmacistReportPdf = useAppSelector(pharmacistPDF);
  const pharmacyReportsPdf = useAppSelector(pharmacyPdf);

  const pharmacistReplacement = useAppSelector(pharmacistReplacementsWeb);
  const pharmacyReplacement = useAppSelector(pharmacyReplacementsWeb);

  const pharmacistNotes = useAppSelector(allNotes);
  const pharmacyNote = useAppSelector(pharmacyNotes);

  const pharmacistHist = useAppSelector(pharmacistHistory);
  const pharmacyHist = useAppSelector(pharmacyHistory);

  const preferredLanguage = useAppSelector(userPreferredLanguage);
  const filterReplacementType = useAppSelector(getMapUserFilterType);
  const filterPostedBy = useAppSelector(getFilterPostedBy);

  const pharmacyFilteredCalendar = filterEventsByType.length > 0
    ? filteredPharmacyEvents
    : activePharmacyCalendar;
  const pharmacistFilteredCalendar = isWatchlisted
    ? filteredPharmacistEvents
    : activePharmacistCalendar;

  const filterPharmacyReplacementType = useMemo(
    () =>
      pharmacyFilteredCalendar.filter(
        filterByReplacementType(filterReplacementType ?? MiscType.All),
      ),
    [pharmacyFilteredCalendar, filterReplacementType],
  );

  const filteredAdminEvents = useMemo(
    () =>
      adminsCalendar.filter(
        filterByReplacementType(filterReplacementType ?? MiscType.All),
      ),
    [adminsCalendar, filterReplacementType],
  );

  const shiftTypeFilter = () => {
    switch (filterShiftType) {
      case ContractTypes.longTerm.toString():
        return {
          adminFilter: filteredAdminEvents
            .filter(filterByLongTerm)
            .filter(filterByPosted(filterPostedBy)),
          pharmacistFilter: pharmacistFilteredCalendar.filter(filterByLongTerm),
          pharmacyFilter:
            filterPharmacyReplacementType.filter(filterByLongTerm),
        };
      case ContractTypes.shortTerm.toString():
        return {
          adminFilter: filteredAdminEvents
            .filter(filterByShortTerm)
            .filter(filterByPosted(filterPostedBy)),
          pharmacistFilter:
            pharmacistFilteredCalendar.filter(filterByShortTerm),
          pharmacyFilter:
            filterPharmacyReplacementType.filter(filterByShortTerm),
        };
      default:
        return {
          adminFilter: filteredAdminEvents.filter(filterByPosted(filterPostedBy)),
          pharmacistFilter: pharmacistFilteredCalendar,
          pharmacyFilter: filterPharmacyReplacementType,
        };
    }
  };

  const { pharmacistFilter, pharmacyFilter, adminFilter } = shiftTypeFilter() ?? {};

  const pharmacistConfig = {
    user: pharmacistUser,
    name: `${pharmacistUser.firstName} ${pharmacistUser.lastName}`,
    calendar: pharmacistFilter,
    reports: activePharmacistReports,
    pdf: pharmacistReportPdf,
    notes: pharmacistNotes,
    replacements: pharmacistReplacement,
    addFavourite,
    removeFavourite,
    history: pharmacistHist,
    legendCalendar: activePharmacistCalendar,
  };

  const userConfig = ToUserTypings({
    [TypeOfUser.pharmacist]: { ...pharmacistConfig },
    [TypeOfUser.technician]: pharmacistConfig,
    [TypeOfUser.student]: pharmacistConfig,
    [TypeOfUser.pharmacy]: {
      user: pharmacyUser,
      name: pharmacyUser.name,
      calendar: pharmacyFilter,
      reports: activePharmacyReports,
      pdf: pharmacyReportsPdf,
      notes: pharmacyNote,
      replacements: pharmacyReplacement,
      addFavourite: pharmacyAddFavourite,
      removeFavourite: pharmacyRemoveFavourite,
      history: pharmacyHist,
      legendCalendar: activePharmacyCalendar,
    },
    [TypeOfUser.admin]: {
      calendar: adminFilter,
      name: adminUser,
      legendCalendar: adminFilter,
    },
    [TypeOfUser.superAdmin]: {
      calendar: adminFilter,
      legendCalendar: adminFilter,
      name: adminUser,
    },
    [TypeOfUser.pharmacyAdmin]: {
      name: generalUserName,
    },
  });

  const {
    name,
    user,
    calendar,
    reports,
    pdf,
    notes,
    replacements,
    addFavourite: userAddFavourite,
    removeFavourite: userRemoveFavourite,
    history,
    legendCalendar,
  } = userConfig[userType] ?? {};

  return {
    name,
    userInfo: user,
    calendar,
    reports,
    pdf,
    notes,
    replacements,
    userType,
    preferredLanguage,
    addFavourite: userAddFavourite,
    removeFavourite: userRemoveFavourite,
    history,
    legendCalendar,
  };
};

export default useUser;
